import { CheckIcon, ChevronDownIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { BiFilter } from 'react-icons/bi'

import {
  Badge,
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Separator,
} from '@/_components/ui'
import { cn } from '@/_lib/utils'
import { useSearchParams } from '@/features/_hooks/use-search-params'
import type { TExposedFacilityDTO } from '@doutorsim/shared/facilities'

interface FacilityFilterProps {
  facilities: TExposedFacilityDTO[]
}

export function FacilityFilter({ facilities }: FacilityFilterProps) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const { searchParams, setSearchParams } = useSearchParams()

  useEffect(() => {
    const params = searchParams as Record<string, unknown>
    const param = (params?.facilityId as string) || ''
    setSelectedIds(new Set(param ? param.split(',') : []))
  }, [searchParams])

  const pushToUrl = (ids: Set<string>) => {
    setSearchParams({
      facilityId: ids.size ? Array.from(ids).join(',') : undefined,
    })
  }

  const handleToggle = (facilityId: string) => {
    const next = new Set(selectedIds)
    if (next.has(facilityId)) {
      next.delete(facilityId)
    } else {
      next.add(facilityId)
    }
    setSelectedIds(next)
    pushToUrl(next)
  }

  const handleClear = () => {
    setSelectedIds(new Set())
    pushToUrl(new Set())
  }

  const selectedCount = selectedIds.size

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-9 font-medium">
          <BiFilter className="mr-2 text-muted-foreground" size={18} />
          Unidade
          {selectedCount > 0 && (
            <>
              <Separator orientation="vertical" className="mx-2 h-4" />
              <Badge className="rounded-sm bg-transparent px-1 font-bold text-brand-green-primary-5 hover:bg-transparent">
                {selectedCount}
              </Badge>
            </>
          )}
          <ChevronDownIcon className="ml-1 h-3.5 w-3.5 text-muted-foreground" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[240px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Pesquise por unidade" className="h-9" />
          <CommandList className="scrollbar">
            <CommandEmpty>Nenhum resultado encontrado.</CommandEmpty>
            <CommandGroup>
              {facilities.map((facility) => {
                const isSelected = selectedIds.has(facility.id)
                return (
                  <CommandItem
                    key={facility.id}
                    value={facility.name}
                    onSelect={() => handleToggle(facility.id)}
                  >
                    <div
                      className={cn(
                        'mr-2 flex h-4 w-4 items-center justify-center rounded-sm border',
                        isSelected
                          ? 'border-primary bg-primary text-primary-foreground'
                          : 'border-muted-foreground/40 [&_svg]:invisible',
                      )}
                    >
                      <CheckIcon className="h-4 w-4" />
                    </div>
                    <span>{facility.name}</span>
                  </CommandItem>
                )
              })}
            </CommandGroup>
          </CommandList>
          {selectedCount > 0 && (
            <>
              <CommandSeparator />
              <div className="flex justify-end p-2">
                <button
                  onClick={handleClear}
                  className="text-xs text-muted-foreground hover:text-foreground"
                >
                  Limpar filtros
                </button>
              </div>
            </>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  )
}
